<template>
  <div>
    <v-container style="max-width: 1185px" class="pb-5">
      <guide-tour config-key="review.googleReviewList" clearAllSubtitle/>
      <dentco-alert-bar
        :message="alertMessage"
        btn-link="https://dentco.notion.site/Google-253bb87c806880628b73e21109a67446"
        btn-title="點我查看 Google 商家授權"
        class="mt-4"
      />
      <v-container>
        <v-row class="mt-4">
          <v-col cols="12" class="text-center">
            <div class="text-h2 font-weight-bold">
              {{ overview?.total?.star || 0 }}
            </div>
            <v-rating
              half-increments
              readonly
              length="5"
              large
              color="yellow darken-2"
              :value="ceilToPoint(overview?.total?.star || 0)"
            />
            <div><span class="text-h5">{{ overview?.total?.count || 0 }}</span> {{ $t('Review.reviewCount') }}</div>
          </v-col>
          <v-col cols="12">
            <div
              class="d-flex align-center"
              :class="{'flex-wrap': $vuetify.breakpoint.xsOnly}"
              style="row-gap: 4px;"
            >
              <v-btn
                v-if="clinicUser"
                color="tiffany-9"
                :block="$vuetify.breakpoint.xsOnly"
                dark
                :loading="updateGoogleReviewLoading"
                @click="refreshGoogleReview"
              >
                立即更新 Google 評論與回覆
              </v-btn>
              <ConfirmDialog
                v-model="toggleConfirmDialog"
                title="操作失敗"
                titleClass="justify-center font-weight-bold text-h5 red-6--text"
                :content="confirmContent"
                contentClass="mt-2 justify-center d-flex black--text"
                confirmBtnText=""
                cancel-btn-text="我知道了，關閉視窗"
                cancel-btn-color="grey-4"
                maxWidth="500"
              />
              <ConfirmDialog
                v-model="toggleInfoDialog"
                title="更新已觸發"
                titleClass="justify-center font-weight-bold text-h5 tiffany-9--text"
                :content="infoContent"
                contentClass="mt-2 justify-center d-flex black--text"
                confirmBtnText=""
                cancel-btn-text="我知道了"
                cancel-btn-color="grey-4"
                maxWidth="500"
              />
              <v-spacer/>
              <div class="d-flex align-center">
                <div style="min-width: fit-content;">
                  評價排序：
                </div>
                <div>
                  <v-select
                    v-model="selectSorted"
                    :items="orderOptions"
                    dense
                    outlined
                    hide-details
                  />
                </div>
              </div>
            </div>
          </v-col>
          <v-col cols="12">
            <ShowAIResponseSetting v-if="userInfo" :userInfo="userInfo" type="google"/>
            <v-divider class="mt-4"/>
            <v-progress-linear :active="loading" indeterminate/>
            <v-tabs v-model="tabs" id="review-list">
              <v-tab>{{ $t('Review.allReview') }}({{ overview?.total?.count || 0 }})</v-tab>
              <v-tab>{{ $t('Review.reviewWithMessage') }}({{ overview?.total?.message || 0 }})</v-tab>
              <v-tab>{{ $t('Review.negativeReview') }}({{ overview?.total?.negative || 0 }})</v-tab>
            </v-tabs>
            <div>
              <template v-if="reviews" v-for="review in reviews">
                <ReviewDetailCard
                  :key="review.id"
                  :review="review"
                  :userInfo="userInfo"
                  review-type="google"
                  @refresh="getReviews"
                />
                <v-divider/>
              </template>
            </div>
            <div>
              <v-pagination
                v-model="page"
                class="my-4"
                :length="maxPage"
                total-visible="10"
              ></v-pagination>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </v-container>
  </div>
</template>

<script>
import GuideTour from "@/components/utils/GuideTour.vue";
import { ceilToPoint } from "@/utils/tool";
import ShowAIResponseSetting from "@/pages/review/ShowAIResponseSetting.vue";
import {
  getGoogleReviewOverviewAPI,
  getGoogleReviewsAPI,
  getInfoAPI,
  getReviewOverviewAPI,
  updateClinicGoogleReviewAPI
} from "@/api";
import { mapGetters } from "vuex";
import ReviewDetailCard from "@/components/review/ReviewDetailCard.vue";
import ConfirmDialog from "@/components/utils/ConfirmDialog.vue";
import DentcoAlertBar from "@/components/utils/DentcoAlertBar.vue";

export default {
  name: 'GoogleReviewPage',

  components: {DentcoAlertBar, ConfirmDialog, ReviewDetailCard, ShowAIResponseSetting, GuideTour},

  data() {
    return {
      userInfo: null,
      tabs: 0,
      overview: null,
      reviews: [],
      page: 1,
      limit: 10,
      maxPage: 1,
      loading: false,
      selectSorted: '0',
      orderOptions: [
        {text: '最新評價', value: '0'},
        {text: '評分(高到低)', value: '1'},
        {text: '評分(低到高)', value: '2'},
        {text: '尚未回覆', value: '3'}
      ],

      updateGoogleReviewLoading: false,
      toggleInfoDialog: false,
      infoContent: '',
      toggleConfirmDialog: false,
      confirmTitle: '',
      confirmContent: '',
      confirmText: '',
      cancelText: '',
      confirmColor: null,
      cancelColor: null,
      confirmFunc: null,
      cancelFunc: null
    }
  },

  computed: {
    ...mapGetters([
      'clinicUser',
      'axiosParamData'
    ]),
    alertMessage() {
      return `請診所按右側按鈕連結文件進行 Google 商家的授權，若有相關問題請 <a target="_blank" href="${this.$enum.DENTCO_CS_LINE}">聯繫客服</a>`
    }
  },

  mounted() {
    this.getPermissionsInfo()
    this.getGoogleReviewOverview()
    this.getReviews()
  },

  watch: {
    page(v) {
      if (v === 1)
        window.scrollTo({top: 0, left: 0, behavior: 'smooth'})
      else
        document.getElementById('review-list').scrollIntoView({behavior: 'smooth', block: 'center'})
      this.getReviews()
    },
    tabs() {
      if (this.page === 1) {
        this.getReviews()
      }
      this.page = 1
    },
    selectSorted() {
      this.getReviews()
    }
  },

  methods: {
    ceilToPoint,
    getPermissionsInfo() {
      getInfoAPI(this.axiosParamData).then(res => {
        this.userInfo = res.data
      })
    },
    getReviews() {
      this.loading = true
      const params = {
        ...this.axiosParamData,
        page: this.page,
        limit: this.limit,
        sort: this.selectSorted,
        kind: this.tabs,
      }
      getGoogleReviewsAPI(params).then(res => {
        this.reviews = res.data.data
        this.maxPage = parseInt(res.data.paginate.page_total)
      }).finally(() => {
        this.loading = false
      })
    },
    getGoogleReviewOverview() {
      getGoogleReviewOverviewAPI(this.axiosParamData).then(res => {
        if (Array.isArray(res.data.data)) {
          this.overview = null
        } else {
          this.overview = res.data.data
        }
      })
    },
    refreshGoogleReview() {
      this.updateGoogleReviewLoading = true
      updateClinicGoogleReviewAPI(this.axiosParamData)
        .then(res => {
          this.infoContent = 'Google 評論更新已在背景進行中，預計 3-10 分鐘後完成。\n完成後請重新整理頁面即可看到最新資料。'
          this.toggleInfoDialog = true
        })
        .catch(e => {
          if (e.response?.status === 404) {
            // 狀態碼是 404
            this.confirmContent = "診所未授權，無法取得Google商家評價。"
            this.toggleConfirmDialog = true
          } else if (e.response?.data?.detail) {
            // API 回傳的 detail 訊息
            this.confirmContent = e.response.data.detail
            this.toggleConfirmDialog = true
          } else {
            // 發生未知錯誤，附加狀態碼與簡短錯誤訊息
            const status = e.response?.status ?? '無狀態碼'
            const msg = e.message ?? '無錯誤訊息'
            const serverData = e.response?.data ? JSON.stringify(e.response.data) : '無回應內容'

            this.confirmContent = `發生未知錯誤 (狀態碼: ${status})，訊息: ${msg}，伺服器回應: ${serverData}。\n請聯繫客服。`
            this.toggleConfirmDialog = true
          }
        })
        .finally(() => {
          this.updateGoogleReviewLoading = false
        })
    }
  }
};
</script>

<style scoped>
/* Your styles here */
</style>
